import * as Sentry from '@sentry/react';
import { datadogRum, DefaultPrivacyLevel, RumInitConfiguration } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { Env, ILogger, Service } from '@bloomays-lib/types.shared';
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router-dom';
import React from 'react';
import { setConsentOnUserAcceptCallback, setConsentOnUserDeclineCallback } from '../helper/cookiebot';
let sentryInit = false;
let useRum = false;

export interface MonitoringOptions {
  sentryDSN?: string;
  useReplay: boolean;
  useReact: boolean;
  release: string;
  allowedTracingUrls: string[];
  datadog: {
    datadogAPIKey?: string;
    datadogRUMAppId?: string;
  };
  env: Env;
  logger?: ILogger;
  service: Service;
}

export const setTrackingConsent = (consent: boolean): void => {
  datadogRum.setTrackingConsent(consent ? 'granted' : 'not-granted');
};

const initMonitoring = (options: MonitoringOptions): void => {
  const sentryDSN = options.sentryDSN;
  const datadogAPIKey = options.datadog.datadogAPIKey;
  if (datadogAPIKey && options.datadog.datadogRUMAppId) {
    const dataDogConfig: Partial<RumInitConfiguration> = {
      site: 'datadoghq.eu',
      service: options.service,
      env: options.env,
      version: options.release,
      sessionSampleRate: options.env === 'production' ? 100 : 0,
      sessionReplaySampleRate: options.env === 'production' ? 100 : 0,
      traceSampleRate: options.env === 'production' ? 100 : 0.0,
      trackUserInteractions: true,
      trackResources: true,
      defaultPrivacyLevel: DefaultPrivacyLevel.MASK_USER_INPUT,
      startSessionReplayRecordingManually: true,
      allowedTracingUrls: options.allowedTracingUrls,
      propagateTraceBaggage: true,
      plugins: options.useReact ? [reactPlugin({ router: true })] : [],
    };
    datadogRum.init({
      ...dataDogConfig,
      applicationId: options.datadog.datadogRUMAppId,
      clientToken: datadogAPIKey,
      trackingConsent: 'not-granted',
    });
    if (options.useReplay) {
      datadogRum.startSessionReplayRecording();
      options.logger?.info('Datadog RUM Init and Session Replay Recording Started ✅', { config: dataDogConfig });
    } else {
      options.logger?.info('Datadog RUM Init Done ✅', { config: dataDogConfig });
    }
    setConsentOnUserAcceptCallback((consent) => setTrackingConsent(consent));
    setConsentOnUserDeclineCallback((consent) => setTrackingConsent(consent));
    useRum = true;
  }
  const integrations = [];
  if (options.useReact) {
    integrations.push(
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    );
  } else {
    integrations.push(Sentry.browserTracingIntegration());
  }
  if (options.useReplay) {
    integrations.push(Sentry.replayIntegration());
  }
  if (sentryDSN && !sentryInit) {
    Sentry.init({
      dsn: sentryDSN,
      integrations,
      tracePropagationTargets: options.allowedTracingUrls,
      // This sets the sample rate to be 10%. You may want this to be 100% while
      // in development and sample at a lower rate in production
      replaysSessionSampleRate: options.env === 'production' ? 0.1 : 1.0,
      // If the entire session is not sampled, use the below sample rate to sample
      // sessions when an error occurs.
      replaysOnErrorSampleRate: options.env === 'production' ? 1.0 : 0.0,
      // We recommend adjusting this value in production, or using tracesSampler
      // for finer control
      tracesSampleRate: options.env === 'production' ? 1.0 : 0.0,
      environment: options.env,
      release: options.release,
      beforeSend(event) {
        // Check if it is an exception, and if so, show the report dialog
        if (event.exception) {
          // Sentry.showReportDialog({ eventId: event.event_id })
        }
        return event;
      },
    });
    sentryInit = true;
    options.logger?.info(`Sentry Init (${integrations.map((i) => i.name).join(', ')}) Done ✅`);
  }
};

const setTransactionName = (transactionName: string | undefined): void => {
  Sentry.getCurrentScope().setTransactionName(transactionName || '');
};
const startTransaction = (name: string): any => {
  return Sentry.startSpan({ name }, (span) => {
    return span;
  });
};

const startStep = (transaction: any, stepName: string): any => {
  return Sentry.startSpan({ name: stepName, op: stepName }, (span) => {
    return span;
  });
};

// Note: stopStep and stopTransaction are no longer needed in Sentry v8
// Spans and transactions are automatically managed

const setUser = (user: { displayName: string; email: string }): void => {
  Sentry.setUser({
    id: user.email,
    email: user.email,
    username: user.displayName,
  });
  if (useRum) {
    datadogRum.setUser({
      id: user?.email,
      name: user?.displayName,
      email: user?.email,
    });
  }
};

const monitorException = (error: any, ctx?: any, flush?: boolean): void => {
  Sentry.captureException(error, ctx);
  if (flush === true) {
    Sentry.flush();
  }
  if (useRum) {
    datadogRum.addError(error, ctx);
  }
};

const withScope = Sentry.withScope;

export { initMonitoring, setTransactionName, startTransaction, startStep, setUser, monitorException, withScope };
